/**
 * Pre-Chat Form Component
 * Collects visitor information before starting chat
 */

export class PreChatForm {
  private container: HTMLElement;
  private onSubmitCallback: (data: { name: string; email: string; message: string }) => void;

  constructor(container: HTMLElement, onSubmit: (data: { name: string; email: string; message: string }) => void) {
    this.container = container;
    this.onSubmitCallback = onSubmit;
    this.render();
  }

  private render() {
    this.container.innerHTML = `
      <div class="prechat-form">
        <div class="online-status-banner" id="online-status-banner">
          <div class="status-indicator online"></div>
          <span id="online-status-text">Checking availability...</span>
        </div>
        <h4>Start a conversation</h4>
        <p>We're here to help! Please fill out the form below to chat with our team.</p>
        
        <form id="prechat-form">
          <div class="form-group">
            <label for="visitor-name">Name *</label>
            <input 
              type="text" 
              id="visitor-name" 
              name="name" 
              required 
              minlength="2"
              placeholder="Your name"
              autocomplete="name"
            />
            <div class="form-error" id="name-error"></div>
          </div>
          
          <div class="form-group">
            <label for="visitor-email">Email *</label>
            <input 
              type="email" 
              id="visitor-email" 
              name="email" 
              required 
              placeholder="your@email.com"
              autocomplete="email"
            />
            <div class="form-error" id="email-error"></div>
          </div>
          
          <div class="form-group">
            <label for="visitor-message">Message (optional)</label>
            <textarea 
              id="visitor-message" 
              name="message" 
              placeholder="How can we help you?"
              maxlength="1000"
            ></textarea>
          </div>
          
          <button type="submit" class="btn-primary" id="start-chat-btn">
            Start Chat
          </button>
        </form>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners() {
    const form = this.container.querySelector('#prechat-form') as HTMLFormElement;
    const nameInput = this.container.querySelector('#visitor-name') as HTMLInputElement;
    const emailInput = this.container.querySelector('#visitor-email') as HTMLInputElement;
    const messageInput = this.container.querySelector('#visitor-message') as HTMLTextAreaElement;
    const submitBtn = this.container.querySelector('#start-chat-btn') as HTMLButtonElement;

    form?.addEventListener('submit', async (e: Event) => {
      e.preventDefault();
      
      // Clear previous errors
      this.clearErrors();

      // Validate
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      const message = messageInput.value.trim();

      let hasError = false;

      if (name.length < 2) {
        this.showError('name-error', 'Name must be at least 2 characters');
        hasError = true;
      }

      if (!this.isValidEmail(email)) {
        this.showError('email-error', 'Please enter a valid email address');
        hasError = true;
      }

      if (hasError) {
        return;
      }

      // Disable form during submission
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<div class="loading-spinner"></div>';

      try {
        await this.onSubmitCallback({ name, email, message });
      } catch (error) {
        // Re-enable form on error
        submitBtn.disabled = false;
        submitBtn.textContent = 'Start Chat';
        this.showError('email-error', 'Failed to start chat. Please try again.');
      }
    });
  }

  private isValidEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  private showError(elementId: string, message: string) {
    const errorEl = this.container.querySelector(`#${elementId}`);
    if (errorEl) {
      errorEl.textContent = message;
    }
  }

  private clearErrors() {
    const errors = this.container.querySelectorAll('.form-error');
    errors.forEach(el => {
      el.textContent = '';
    });
  }

  setOnlineStatus(isOnline: boolean, message: string) {
    const statusIndicator = this.container.querySelector('#online-status-banner .status-indicator');
    const statusText = this.container.querySelector('#online-status-text');
    
    if (statusIndicator) {
      statusIndicator.classList.toggle('online', isOnline);
      statusIndicator.classList.toggle('offline', !isOnline);
    }
    
    if (statusText) {
      statusText.textContent = message;
    }
  }

  destroy() {
    this.container.innerHTML = '';
  }
}
